import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Zap,
  ArrowRight,
  Sparkles,
  Globe,
  Package,
  Shield,
  LineChart,
  TrendingUp,
  Mail,
  Check,
  Loader2,
  BookOpen,
  ChevronRight,
  AlertTriangle,
  HelpCircle,
  DollarSign,
  FileSearch,
  Upload,
  BarChart3,
  FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';

const PreLaunchWrapper = () => {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [position, setPosition] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [activePain, setActivePain] = useState(null);

  const painPoints = [
    {
      id: 'classification',
      icon: <HelpCircle className="w-6 h-6" />,
      question: "Are you confident your HS codes are correct?",
      chipClass: 'bg-blue-50 text-blue-700',
      iconClass: 'bg-blue-50 text-blue-700',
      linkClass: 'text-blue-700',
      detail: {
        headline: "Most importers are misclassifying without knowing it",
        body: "With 5,387+ HS headings that change quarterly, manual classification is a gamble. Even customs brokers get it wrong 15-25% of the time. Every misclassified product means you're either overpaying duty or building up compliance risk.",
        solution: "Decoded uses multi-model AI to cross-reference your product descriptions against live tariff schedules — flagging mismatches, suggesting optimal codes, and scoring confidence so you know exactly where to act."
      }
    },
    {
      id: 'money',
      icon: <DollarSign className="w-6 h-6" />,
      question: "Do you know how much you've lost to incorrect tariffs?",
      chipClass: 'bg-amber-50 text-amber-700',
      iconClass: 'bg-amber-50 text-amber-700',
      linkClass: 'text-amber-700',
      detail: {
        headline: "The average importer overpays 12-25% in duties annually",
        body: "Tariff misclassification doesn't just cost pennies — it compounds across every shipment, every SKU, every quarter. Most companies have never audited their classifications, which means years of overpayments sitting unrecovered.",
        solution: "Decoded quantifies your exposure instantly. Upload your catalog and see exactly where you've been overpaying, what's recoverable, and how much you could save by switching to the correct codes."
      }
    },
    {
      id: 'sourcing',
      icon: <AlertTriangle className="w-6 h-6" />,
      question: "Is your supply chain optimized for today's tariff landscape?",
      chipClass: 'bg-violet-50 text-violet-700',
      iconClass: 'bg-violet-50 text-violet-700',
      linkClass: 'text-violet-700',
      detail: {
        headline: "IEEPA tariffs just changed everything",
        body: "With duties up to 145% on imports from 31 countries, sourcing decisions made last year may now be costing you a fortune. FTAs, preferential programs, and alternative origins could slash your landed cost — but only if you know where to look.",
        solution: "Decoded compares sourcing across 30+ countries, factors in FTAs and Section 301 duties, and shows you the optimal origin for every product in your catalog — with real dollar savings."
      }
    }
  ];

  const features = [
    { icon: <Sparkles className="w-5 h-5" />, title: "AI-Powered Classification", description: "Intelligent HS code suggestions powered by multi-model AI for single products and bulk catalogs." },
    { icon: <Globe className="w-5 h-5" />, title: "Smart Sourcing Optimizer", description: "Find optimal sourcing countries based on tariffs, trade agreements, and cost analysis." },
    { icon: <Package className="w-5 h-5" />, title: "Origin & Preference Engine", description: "Validate country of origin, check FTA eligibility, and model sourcing scenarios." },
    { icon: <Shield className="w-5 h-5" />, title: "Classification Governance", description: "Audit trails, inconsistency detection, confidence tracking, and tariff impact alerts." },
    { icon: <LineChart className="w-5 h-5" />, title: "Financial Impact Analysis", description: "Quantify duty overpayments, identify recoverable savings, and track exposure." },
    { icon: <TrendingUp className="w-5 h-5" />, title: "Landed Cost Optimization", description: "Calculate effective tariff rates including Section 301 duties and FTA benefits." }
  ];

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await apiGet('/api/waitlist/count', { auth: false });
        if (data) setWaitlistCount(data.count);
      } catch { /* ignore */ }
    };
    fetchCount();
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, []);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const data = await apiPost('/api/waitlist/join', { email, company }, { auth: false });
      setJoined(true);
      setPosition(data.position);
      setWaitlistCount(prev => Math.max(prev, data.position));
      toast.success(data.status === 'already_joined' ? "You're already on the waitlist!" : "You're on the list! Check your email.");
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Something went wrong');
      } else {
        toast.error('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {/* IEEPA Announcement Bar */}
      <div className="fixed top-0 left-0 right-0 z-[55] bg-amber-50 border-b border-amber-100" data-testid="ieepa-announcement-bar">
        <a href="#ieepa-section" className="block hover:bg-amber-100/60 transition-colors">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">IEEPA</span>
            <p className="text-sm text-amber-900 text-center">
              <span className="font-semibold">Executive Order 14257</span>
              <span className="hidden sm:inline"> (IEEPA Reciprocal Tariffs)</span>
              {' '}&mdash; $166B in duties paid since April 2025 may be recoverable.
              <span className="hidden md:inline"> Are you exposed?</span>
            </p>
            <span className="text-amber-700 text-xs font-medium whitespace-nowrap">Learn more <ChevronRight className="inline w-3 h-3" /></span>
          </div>
        </a>
      </div>

      {/* Navigation — mirrors Resources.jsx exactly */}
      <nav className="fixed top-[42px] left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="#" onClick={(e) => {
            e.preventDefault();
            if (window.location.hash) {
              window.history.replaceState(null, '', window.location.pathname);
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }} className="flex items-center gap-2 cursor-pointer" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-bold text-lg text-slate-900">Decoded</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="#features" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Features</a>
            <Link to="/resources" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Resources</Link>
            <a href="#waitlist" data-testid="nav-join-waitlist-btn">
              <Button size="sm" className="bg-[#0B3D91] hover:bg-[#082A66]">Join Waitlist</Button>
            </a>
          </div>
        </div>
      </nav>

      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Hero — matches Resources scale */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4" data-testid="launch-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
              Launching Soon
            </div>
            <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-testid="hero-title">
              The <span className="text-amber-600">$166 Billion</span> Opportunity in Your Import Data
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              AI-powered classification and refund analysis for importers who want to capture what&apos;s theirs.
            </p>
          </div>

          {/* Pain Points */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-testid="pain-points-grid">
            {painPoints.map((pain) => (
              <button
                key={pain.id}
                onClick={() => setActivePain(activePain === pain.id ? null : pain.id)}
                className={`text-left bg-white rounded-2xl shadow-sm border overflow-hidden transition-all cursor-pointer group p-6 ${
                  activePain === pain.id
                    ? 'border-blue-200 shadow-md'
                    : 'border-slate-100 hover:shadow-md hover:border-blue-200'
                }`}
                data-testid={`pain-point-${pain.id}`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${pain.iconClass}`}>
                  {pain.icon}
                </div>
                <p className="font-outfit font-semibold text-slate-900 text-base leading-snug mb-3">
                  {pain.question}
                </p>
                <span className={`text-sm font-semibold ${pain.linkClass}`}>
                  {activePain === pain.id ? 'Close' : 'Find out more'}
                  <ChevronRight className={`inline w-3.5 h-3.5 ml-1 transition-transform ${activePain === pain.id ? 'rotate-90' : ''}`} />
                </span>
              </button>
            ))}
          </div>

          {/* Expanded Pain Detail */}
          {activePain && (() => {
            const pain = painPoints.find(p => p.id === activePain);
            return (
              <div
                className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 mb-12"
                style={{ animation: 'fadeSlideIn 0.3s ease-out' }}
                data-testid="pain-detail-panel"
              >
                <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-3">{pain.detail.headline}</h3>
                <p className="text-base text-slate-600 leading-relaxed mb-5">{pain.detail.body}</p>
                <div className={`rounded-xl p-5 mb-5 ${pain.chipClass}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${pain.linkClass}`}>What Decoded does about it</p>
                  <p className="text-slate-700 text-sm leading-relaxed">{pain.detail.solution}</p>
                </div>
                <a href="#waitlist" data-testid="pain-detail-cta">
                  <Button size="sm" className="bg-[#0B3D91] hover:bg-[#082A66]">
                    Get Early Access <ArrowRight className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </a>
              </div>
            );
          })()}

          {/* Waitlist Form */}
          <section id="waitlist" className="max-w-lg mx-auto mb-16" data-testid="waitlist-section">
            {joined ? (
              <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-8 text-center" data-testid="waitlist-success">
                <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-2">You&apos;re in.</h3>
                <p className="text-base text-slate-600">
                  Position <span className="font-bold text-blue-700">#{position}</span> on the waitlist.
                  We&apos;ll notify you the moment we launch.
                </p>
              </div>
            ) : (
              <form onSubmit={handleJoin} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex flex-col sm:flex-row gap-3 mb-3">
                  <Input
                    type="email"
                    placeholder="Work email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 h-11 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    data-testid="waitlist-email-input"
                  />
                  <Input
                    type="text"
                    placeholder="Company (optional)"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="flex-1 sm:max-w-[180px] h-11 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    data-testid="waitlist-company-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full h-11 bg-[#0B3D91] hover:bg-[#082A66] text-sm font-semibold rounded-xl"
                  data-testid="waitlist-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Joining...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" />Get Early Access</>
                  )}
                </Button>
              </form>
            )}

            {waitlistCount > 0 && (
              <div className="flex items-center justify-center gap-2 mt-5" data-testid="waitlist-counter">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                <p className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-700">{waitlistCount.toLocaleString()}</span> trade professionals on the waitlist
                </p>
              </div>
            )}
          </section>

          {/* Features */}
          <section id="features" className="mb-16">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Sparkles className="w-4 h-4" />
                Platform
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                What you&apos;ll get access to
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                Everything you need to classify, quantify and recover — in one platform.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="features-grid">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md hover:border-blue-200 transition-all group"
                  data-testid={`prelaunch-feature-${index}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                    {feature.icon}
                  </div>
                  <h3 className="font-outfit font-semibold text-slate-900 text-base mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Resources CTA */}
          <section className="max-w-3xl mx-auto mb-16">
            <Link to="/resources" className="block">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md hover:border-blue-200 transition-all flex items-center justify-between group" data-testid="resources-cta">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-slate-900 text-base mb-0.5">Trade Intelligence Hub</h3>
                    <p className="text-sm text-slate-600">Read our latest articles on tariff strategy and compliance</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
              </div>
            </Link>
          </section>

          {/* IEEPA $166B Detail Section */}
          <section id="ieepa-section" className="max-w-4xl mx-auto mb-16" data-testid="ieepa-detail-section">
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-8 md:p-10">
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                    $166 Billion in Tariff Refunds. Are You Eligible?
                  </h2>
                  <p className="text-sm text-amber-700 font-medium">
                    Executive Order 14257 &mdash; International Emergency Economic Powers Act (IEEPA) Reciprocal Tariffs
                  </p>
                </div>
              </div>

              {/* Context */}
              <div className="space-y-4 mb-8">
                <p className="text-base text-slate-700 leading-relaxed">
                  Following the 2025 IEEPA reciprocal tariff actions, an estimated <span className="text-slate-900 font-semibold">$166 billion</span> in U.S. import duties may be eligible for drawback and recovery. This applies to any company that imports into the United States &mdash; whether you&apos;re based in the U.S., Europe, Asia, or anywhere else.
                </p>
                <p className="text-base text-slate-600 leading-relaxed">
                  If you&apos;ve exported goods into the American market under DDP terms, operate a U.S. import entity, or are the importer of record for U.S.-bound shipments &mdash; you may have overpaid and may be eligible to reclaim.
                </p>
              </div>

              {/* How Decoded Helps */}
              <div className="bg-[#F8FAFC] rounded-xl border border-slate-100 p-6 mb-6">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-4">How Decoded Helps You Find Out</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: <Upload className="w-4 h-4 text-blue-700" />, tone: 'blue', title: 'Upload your trade data', body: 'Import records, ERP exports, or customs declarations' },
                    { icon: <FileSearch className="w-4 h-4 text-blue-700" />, tone: 'blue', title: 'Instant eligibility scan', body: 'AI analyzes transactions against IEEPA tariff schedules' },
                    { icon: <BarChart3 className="w-4 h-4 text-amber-700" />, tone: 'amber', title: 'See your exposure', body: 'Know exactly which entries are reclaimable and how much' },
                    { icon: <FileDown className="w-4 h-4 text-amber-700" />, tone: 'amber', title: 'Export-ready output', body: 'Generate documentation needed to file your claim' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${item.tone === 'blue' ? 'bg-blue-50' : 'bg-amber-50'}`}>
                        {item.icon}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Global note + CTA */}
              <p className="text-xs text-slate-500 italic mb-6 leading-relaxed">
                Applies to U.S. importers, European exporters selling into the U.S., and any global company with a U.S. import operation. No consultants. No six-week engagements. Answers in seconds.
              </p>
              <a href="#waitlist" data-testid="ieepa-cta-btn">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                  Join the Waitlist &mdash; Run Your Analysis First <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="max-w-2xl mx-auto text-center py-16">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Be first to know when we launch
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mb-8 max-w-md mx-auto">
              Early access members get priority onboarding and dedicated support.
            </p>
            {!joined && (
              <a href="#waitlist" data-testid="bottom-cta-waitlist-btn">
                <Button className="bg-[#0B3D91] hover:bg-[#082A66] px-8 h-12 rounded-xl text-sm font-semibold">
                  Join the Waitlist <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            )}
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-outfit font-bold text-lg text-slate-900">Decoded</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/resources" className="text-slate-600 hover:text-blue-600 transition-colors">Resources</Link>
              <Link to="/terms" className="text-slate-600 hover:text-blue-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="text-slate-600 hover:text-blue-600 transition-colors">Privacy</Link>
            </div>
            <p className="text-slate-500 text-xs">
              &copy; {new Date().getFullYear()} Decoded. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Inline keyframe for detail panel animation */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PreLaunchWrapper;
